import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios, { getApiUrl } from "@/lib/api";
import {
  Users,
  Activity,
  UserPlus,
  FlaskConical,
  TrendingUp,
  Loader2,
  Search,
  ChevronDown,
  ChevronUp,
  Clock,
  Gamepad2,
  Trophy,
  BarChart3,
  Monitor,
  Crown,
  Tag,
  Check,
  X,
  ThumbsUp,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const API = `${getApiUrl()}/api`;

const COLORS = ["#8b5cf6", "#06b6d4", "#f59e0b", "#ef4444", "#22c55e", "#ec4899", "#3b82f6", "#f97316"];

// Side-by-side pie + labeled data list. Every slice gets a legible row with
// color swatch, name, count, and percent — nothing overlaps and nothing hides.
function LabeledPie({ data, nameKey, dataKey = "count", height = 380 }) {
  const total = (data || []).reduce((s, r) => s + (r[dataKey] || 0), 0) || 1;

  // Inner slice label. Only draws when the slice is big enough to hold
  // readable text; smaller slices remain identifiable via the side list.
  const renderSliceLabel = (props) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, percent } = props;
    if (percent < 0.06) return null;
    const RAD = Math.PI / 180;
    const r = innerRadius + (outerRadius - innerRadius) * 0.62;
    const x = cx + r * Math.cos(-midAngle * RAD);
    const y = cy + r * Math.sin(-midAngle * RAD);
    const name = props[nameKey];
    const pct = (percent * 100).toFixed(0);
    return (
      <text
        x={x}
        y={y}
        fill="#fff"
        fontSize={10}
        fontWeight={600}
        textAnchor="middle"
        dominantBaseline="central"
        style={{ pointerEvents: "none", textShadow: "0 1px 2px rgba(0,0,0,0.6)" }}
      >
        <tspan x={x} dy="-0.4em">{name}</tspan>
        <tspan x={x} dy="1.1em">{pct}%</tspan>
      </text>
    );
  };

  return (
    <div className="flex items-stretch gap-3">
      <div className="flex-shrink-0" style={{ width: 220 }}>
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            <Pie
              data={data || []}
              dataKey={dataKey}
              nameKey={nameKey}
              cx="50%"
              cy="50%"
              outerRadius={90}
              stroke="#111"
              strokeWidth={1}
              label={renderSliceLabel}
              labelLine={false}
              isAnimationActive={false}
            >
              {(data || []).map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div
        className="flex-1 min-w-0 overflow-y-auto pr-1"
        style={{ maxHeight: height }}
      >
        <ul className="space-y-1">
          {(data || []).map((row, i) => {
            const pct = (((row[dataKey] || 0) / total) * 100);
            const pctLabel = pct >= 10 ? pct.toFixed(0) : pct.toFixed(1);
            return (
              <li
                key={`${row[nameKey]}-${i}`}
                className="flex items-center gap-2 text-xs text-white/70 leading-snug"
              >
                <span
                  className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0"
                  style={{ background: COLORS[i % COLORS.length] }}
                />
                <span className="truncate flex-1" title={row[nameKey]}>{row[nameKey]}</span>
                <span className="text-white/40 tabular-nums flex-shrink-0">
                  {(row[dataKey] || 0).toLocaleString()}
                </span>
                <span className="text-white/50 tabular-nums w-11 text-right flex-shrink-0">
                  {pctLabel}%
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color = "text-purple-400" }) {
  return (
    <Card className="bg-[#111] border-white/5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <CardContent className="pt-5 pb-4 px-5">
        <div className="flex items-center gap-3 mb-2">
          <div className={`p-2 rounded-lg bg-white/5 ${color}`}>
            <Icon className="w-4 h-4" />
          </div>
          <span className="text-white/40 text-xs uppercase tracking-wider">{label}</span>
        </div>
        <p className="text-2xl font-bold text-white">{value?.toLocaleString?.() ?? value}</p>
        {sub && <p className="text-xs text-white/30 mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function ChartCard({ title, children }) {
  return (
    <Card className="bg-[#111] border-white/5">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-white/50 font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-xs">
      <p className="text-white/60 mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color }} className="font-medium">
          {entry.name}: {entry.value?.toLocaleString()}
        </p>
      ))}
    </div>
  );
};

function timeAgo(dateStr) {
  if (!dateStr) return "Never";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const ACTION_LABELS = {
  login: "Logged in",
  sync: "Synced profile",
  page_visit: "Visited page",
};

export default function SuperAdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [charts, setCharts] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [newUsers, setNewUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [userSort, setUserSort] = useState("created_at");
  const [userSortDir, setUserSortDir] = useState("desc");
  const [expandedSections, setExpandedSections] = useState({
    newUsers: true,
    activity: true,
    users: true,
    tagSuggestions: true,
  });

  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [tagSuggestionsCount, setTagSuggestionsCount] = useState(0);
  const [tagFilter, setTagFilter] = useState("pending");
  const [tagActionLoading, setTagActionLoading] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, chartRes, usersRes, newUsersRes, activityRes, tagSugRes] = await Promise.all([
        axios.get(`${API}/super-admin/dashboard`),
        axios.get(`${API}/super-admin/charts`),
        axios.get(`${API}/super-admin/users?limit=100&sort_by=${userSort}&sort_dir=${userSortDir}`),
        axios.get(`${API}/super-admin/new-users?days=30`),
        axios.get(`${API}/super-admin/activity?limit=50`),
        axios.get(`${API}/super-admin/tag-suggestions?status=${tagFilter}`).catch(() => ({ data: { suggestions: [], pendingCount: 0 } })),
      ]);
      setDashboard(dashRes.data);
      setCharts(chartRes.data);
      setUsers(usersRes.data.users);
      setUsersTotal(usersRes.data.total);
      setNewUsers(newUsersRes.data.users);
      setActivity(activityRes.data.activities);
      setTagSuggestions(tagSugRes.data.suggestions || []);
      setTagSuggestionsCount(tagSugRes.data.pendingCount || 0);
    } catch (err) {
      toast.error("Failed to load super admin data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [userSort, userSortDir, tagFilter]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const toggleSection = (s) => setExpandedSections((prev) => ({ ...prev, [s]: !prev[s] }));

  const handleSort = (field) => {
    if (userSort === field) {
      setUserSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setUserSort(field);
      setUserSortDir("desc");
    }
  };

  const toggleProOverride = async (userId) => {
    try {
      const res = await axios.post(`${API}/super-admin/users/${userId}/toggle-pro`);
      const newVal = res.data.proOverride;
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, proOverride: newVal } : u));
      toast.success(`Pro ${newVal ? "granted" : "revoked"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to toggle Pro");
    }
  };

  const toggleTestAccount = async (userId) => {
    try {
      const res = await axios.post(`${API}/super-admin/users/${userId}/toggle-test`);
      const newVal = res.data.isTestAccount;
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, isTestAccount: newVal, isExcluded: newVal || u.isAdmin } : u));
      toast.success(`${newVal ? "Marked as" : "Removed"} test account — usage data will update on refresh`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to toggle test account");
    }
  };

  const refreshTagSuggestions = async (filter) => {
    try {
      const res = await axios.get(`${API}/super-admin/tag-suggestions?status=${filter || tagFilter}`);
      setTagSuggestions(res.data.suggestions || []);
      setTagSuggestionsCount(res.data.pendingCount || 0);
    } catch (err) {
      console.error("Failed to refresh tag suggestions", err);
    }
  };

  const handleTagAction = async (suggestionId, action) => {
    setTagActionLoading(suggestionId);
    try {
      const res = await axios.post(`${API}/super-admin/tag-suggestions/${suggestionId}/${action}`);
      toast.success(res.data.message || `Suggestion ${action}d`);
      await refreshTagSuggestions();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} suggestion`);
    } finally {
      setTagActionLoading(null);
    }
  };

  const filteredUsers = userSearch
    ? users.filter(
        (u) =>
          u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
          u.email?.toLowerCase().includes(userSearch.toLowerCase()) ||
          u.username?.toLowerCase().includes(userSearch.toLowerCase())
      )
    : users;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white/50" />
      </div>
    );
  }

  const d = dashboard || {};

  return (
    <div className="space-y-6" data-testid="super-admin-dashboard">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl text-white">Command Center</h1>
        <p className="text-white/40 text-sm mt-1">User traffic, activity, and platform analytics</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Users} label="Total Users" value={d.totalUsers} sub={`+${d.newUsersWeek} this week`} />
        <StatCard icon={Activity} label="Active Today" value={d.activeToday} sub={`${d.activeWeek} this week`} color="text-cyan-400" />
        <StatCard icon={UserPlus} label="New This Month" value={d.newUsersMonth} sub={`${d.newUsersToday} today`} color="text-emerald-400" />
        <StatCard icon={TrendingUp} label="Total Activities" value={d.totalActivities} color="text-amber-400" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Gamepad2} label="Total Games" value={d.totalGames} color="text-blue-400" />
        <StatCard icon={Trophy} label="Total Achievements" value={d.totalAchievements} color="text-yellow-400" />
        <StatCard icon={Monitor} label="Active This Week" value={d.activeWeek} color="text-pink-400" />
        <StatCard icon={BarChart3} label="Active This Month" value={d.activeMonth} color="text-orange-400" />
      </div>

      {/* Charts Row */}
      {charts && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* User Registrations */}
          <ChartCard title="New User Registrations (30 days)">
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={charts.registrations}>
                <defs>
                  <linearGradient id="regGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                <XAxis dataKey="date" tick={{ fill: "#666", fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fill: "#666", fontSize: 10 }} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="count" name="Registrations" stroke="#8b5cf6" fill="url(#regGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Login Sessions */}
          <ChartCard title="Login Sessions (30 days)">
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={charts.sessions}>
                <defs>
                  <linearGradient id="sessGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                <XAxis dataKey="date" tick={{ fill: "#666", fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fill: "#666", fontSize: 10 }} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="count" name="Sessions" stroke="#06b6d4" fill="url(#sessGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Logins by Hour */}
          <ChartCard title="Logins by Hour of Day (Last 30 days)">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={charts.loginsByHour}>
                <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                <XAxis dataKey="hour" tick={{ fill: "#666", fontSize: 10 }} tickFormatter={(v) => `${v}:00`} />
                <YAxis tick={{ fill: "#666", fontSize: 10 }} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="Logins" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Platform Breakdown */}
          <ChartCard title="Games by Platform">
            <LabeledPie data={d.platformBreakdown || []} nameKey="platform" />
          </ChartCard>

          {/* Activity by Day of Week */}
          {charts.activityByDow?.some((d) => d.count > 0) && (
            <ChartCard title="Activity by Day of Week">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={charts.activityByDow}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                  <XAxis dataKey="day" tick={{ fill: "#666", fontSize: 10 }} />
                  <YAxis tick={{ fill: "#666", fontSize: 10 }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Activity" fill="#22c55e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}

          {/* Activity by Type */}
          {charts.activityByType?.length > 0 && (
            <ChartCard title="Activity Breakdown">
              <LabeledPie data={charts.activityByType} nameKey="type" />
            </ChartCard>
          )}

          {/* Top Pages */}
          {charts.topPages?.length > 0 && (
            <ChartCard title="Most Visited Pages">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={charts.topPages} layout="vertical" margin={{ left: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#222" horizontal={false} />
                  <XAxis type="number" tick={{ fill: "#666", fontSize: 10 }} allowDecimals={false} />
                  <YAxis dataKey="page" type="category" tick={{ fill: "#888", fontSize: 10 }} width={80} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Visits" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}

          {/* Unique Visitors per Page */}
          {charts.pageVisitors?.length > 0 && (
            <ChartCard title="Unique Visitors by Page (30 days)">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={charts.pageVisitors} layout="vertical" margin={{ left: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#222" horizontal={false} />
                  <XAxis type="number" tick={{ fill: "#666", fontSize: 10 }} allowDecimals={false} />
                  <YAxis dataKey="page" type="category" tick={{ fill: "#888", fontSize: 10 }} width={80} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="uniqueVisitors" name="Unique Visitors" fill="#06b6d4" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
        </div>
      )}

      {/* Tag Suggestions Section */}
      <Card className="bg-[#111] border-amber-500/20">
        <CardHeader
          className="cursor-pointer"
          onClick={() => toggleSection("tagSuggestions")}
          data-testid="tag-suggestions-section"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-amber-400" />
              <CardTitle className="text-base text-amber-400">
                Community Tag Suggestions
              </CardTitle>
              {tagSuggestionsCount > 0 && (
                <span className="ml-1 px-2 py-0.5 text-[10px] font-bold rounded-full bg-amber-500/20 text-amber-300" data-testid="tag-suggestions-badge">
                  {tagSuggestionsCount} pending
                </span>
              )}
            </div>
            {expandedSections.tagSuggestions ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
          </div>
        </CardHeader>
        {expandedSections.tagSuggestions && (
          <CardContent>
            {/* Filter tabs */}
            <div className="flex gap-1 mb-4" data-testid="tag-suggestions-filters">
              {["pending", "approved", "dismissed", "all"].map((f) => (
                <button
                  key={f}
                  onClick={(e) => { e.stopPropagation(); setTagFilter(f); refreshTagSuggestions(f); }}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                    tagFilter === f
                      ? "bg-amber-500/20 text-amber-300"
                      : "bg-white/5 text-white/40 hover:bg-white/10 hover:text-white/60"
                  }`}
                  data-testid={`tag-filter-${f}`}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>

            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {tagSuggestions.length === 0 ? (
                <p className="text-white/30 text-sm text-center py-6" data-testid="tag-suggestions-empty">
                  No {tagFilter === "all" ? "" : tagFilter} tag suggestions
                </p>
              ) : (
                tagSuggestions.map((s) => (
                  <div
                    key={s.id}
                    className="flex items-start justify-between gap-3 py-3 px-4 rounded-lg bg-white/[0.02] border border-white/[0.04] hover:border-white/[0.08] transition-colors"
                    data-testid={`tag-suggestion-${s.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className="inline-block px-2 py-0.5 rounded text-[11px] font-medium"
                          style={{ backgroundColor: `${s.tagColor}22`, color: s.tagColor, border: `1px solid ${s.tagColor}44` }}
                        >
                          {s.tagName}
                        </span>
                        <span className="text-white/20 text-[10px]">on</span>
                        <span className="text-white text-xs font-medium truncate">{s.achievementName}</span>
                      </div>
                      <p className="text-white/30 text-[11px] mt-1 truncate">
                        {s.masterGameName}
                        {s.achievementDescription && (
                          <span className="text-white/15 ml-1.5">— {s.achievementDescription}</span>
                        )}
                      </p>
                      <div className="flex items-center gap-3 mt-1.5">
                        <span className="flex items-center gap-1 text-[10px] text-amber-400/70">
                          <ThumbsUp className="w-3 h-3" />
                          {s.voteCount} {s.voteCount === 1 ? "vote" : "votes"}
                        </span>
                        <span className="text-[10px] text-white/20">
                          {s.voters?.map(v => v.email).join(", ")}
                        </span>
                        {s.status !== "pending" && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                            s.status === "approved" ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"
                          }`}>
                            {s.status}
                          </span>
                        )}
                      </div>
                    </div>
                    {s.status === "pending" && (
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          onClick={() => handleTagAction(s.id, "approve")}
                          disabled={tagActionLoading === s.id}
                          className="p-1.5 rounded-md bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors disabled:opacity-40"
                          title="Approve — apply tag to MDB"
                          data-testid={`approve-tag-${s.id}`}
                        >
                          {tagActionLoading === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                        </button>
                        <button
                          onClick={() => handleTagAction(s.id, "dismiss")}
                          disabled={tagActionLoading === s.id}
                          className="p-1.5 rounded-md bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-40"
                          title="Dismiss suggestion"
                          data-testid={`dismiss-tag-${s.id}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* New Users Alert Section */}
      <Card className="bg-[#111] border-emerald-500/20">
        <CardHeader
          className="cursor-pointer"
          onClick={() => toggleSection("newUsers")}
          data-testid="new-users-section"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-400" />
              <CardTitle className="text-base text-emerald-400">
                New Users ({newUsers.length})
              </CardTitle>
            </div>
            {expandedSections.newUsers ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
          </div>
        </CardHeader>
        {expandedSections.newUsers && (
          <CardContent>
            <div className="space-y-1 max-h-[300px] overflow-y-auto">
              {newUsers.length === 0 ? (
                <p className="text-white/30 text-sm text-center py-4">No new users in the last 30 days</p>
              ) : (
                newUsers.map((u) => (
                  <div
                    key={u.user_id}
                    className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-white/5 transition-colors"
                    data-testid={`new-user-${u.user_id}`}
                  >
                    <div className="flex items-center gap-3">
                      {u.picture ? (
                        <img src={u.picture} alt="" className="w-7 h-7 rounded-full" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-emerald-600/30 flex items-center justify-center text-emerald-400 text-xs font-medium">
                          {u.name?.[0] || "?"}
                        </div>
                      )}
                      <div>
                        <p className="text-sm text-white font-medium">{u.name}</p>
                        <p className="text-xs text-white/30">{u.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-white/40">{timeAgo(u.created_at)}</p>
                      <p className="text-[10px] text-white/20">{formatDate(u.created_at)}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Recent Activity Feed */}
      <Card className="bg-[#111] border-cyan-500/20">
        <CardHeader
          className="cursor-pointer"
          onClick={() => toggleSection("activity")}
          data-testid="activity-section"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-cyan-400" />
              <CardTitle className="text-base text-cyan-400">
                Recent Activity ({activity.length})
              </CardTitle>
            </div>
            {expandedSections.activity ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
          </div>
        </CardHeader>
        {expandedSections.activity && (
          <CardContent>
            <div className="space-y-1 max-h-[400px] overflow-y-auto">
              {activity.length === 0 ? (
                <p className="text-white/30 text-sm text-center py-4">No activity recorded yet. Activity tracking starts now.</p>
              ) : (
                activity.map((a, i) => (
                  <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-white/5 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`p-1.5 rounded-md ${
                        a.action === "login" ? "bg-purple-500/10 text-purple-400" :
                        a.action === "sync" ? "bg-blue-500/10 text-blue-400" :
                        "bg-white/5 text-white/40"
                      }`}>
                        {a.action === "login" ? <Users className="w-3.5 h-3.5" /> :
                         a.action === "sync" ? <Gamepad2 className="w-3.5 h-3.5" /> :
                         <Clock className="w-3.5 h-3.5" />}
                      </div>
                      <div>
                        <p className="text-sm text-white">
                          <span className="font-medium">{a.userName}</span>
                          <span className="text-white/40 ml-1.5">
                            {a.action === "page_visit" ? (
                              a.details?.page ? (
                                <>
                                  Visited{" "}
                                  <Link
                                    to={a.details.page}
                                    className="text-white/60 hover:text-yellow-400 hover:underline transition-colors"
                                    data-testid={`activity-page-link-${i}`}
                                  >
                                    {a.details?.pageLabel || "page"}
                                  </Link>
                                </>
                              ) : (
                                `Visited ${a.details?.pageLabel || "page"}`
                              )
                            ) : (
                              ACTION_LABELS[a.action] || a.action
                            )}
                            {a.details?.platform && ` (${a.details.platform})`}
                          </span>
                        </p>
                        <p className="text-[10px] text-white/20">{a.userEmail}</p>
                      </div>
                    </div>
                    <span className="text-xs text-white/30 whitespace-nowrap">{timeAgo(a.timestamp)}</span>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* User Directory */}
      <Card className="bg-[#111] border-white/5">
        <CardHeader
          className="cursor-pointer"
          onClick={() => toggleSection("users")}
          data-testid="users-section"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-400" />
              <CardTitle className="text-base text-purple-400">
                All Users ({usersTotal})
              </CardTitle>
            </div>
            {expandedSections.users ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
          </div>
        </CardHeader>
        {expandedSections.users && (
          <CardContent>
            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <input
                type="text"
                placeholder="Search users..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-black/40 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-purple-500/40"
                data-testid="user-search-input"
              />
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-white/30 text-xs border-b border-white/5">
                    <th className="text-left py-2 px-2 font-medium cursor-pointer hover:text-white/60" onClick={() => handleSort("name")}>
                      User {userSort === "name" && (userSortDir === "desc" ? "↓" : "↑")}
                    </th>
                    <th className="text-left py-2 px-2 font-medium cursor-pointer hover:text-white/60 hidden sm:table-cell" onClick={() => handleSort("created_at")}>
                      Joined {userSort === "created_at" && (userSortDir === "desc" ? "↓" : "↑")}
                    </th>
                    <th className="text-center py-2 px-2 font-medium">Sessions</th>
                    <th className="text-center py-2 px-2 font-medium hidden md:table-cell">Games</th>
                    <th className="text-center py-2 px-2 font-medium hidden md:table-cell">Achievements</th>
                    <th className="text-center py-2 px-2 font-medium">Pro</th>
                    <th className="text-center py-2 px-2 font-medium">Test</th>
                    <th className="text-right py-2 px-2 font-medium">Last Active</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr key={u.user_id} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors">
                      <td className="py-2.5 px-2">
                        <div className="flex items-center gap-2.5">
                          {u.picture ? (
                            <img src={u.picture} alt="" className="w-6 h-6 rounded-full" />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-purple-600/30 flex items-center justify-center text-purple-300 text-[10px] font-medium">
                              {u.name?.[0] || "?"}
                            </div>
                          )}
                          <div>
                            <p className="text-white font-medium text-xs">{u.name}</p>
                            <p className="text-white/20 text-[10px]">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-2.5 px-2 text-white/40 text-xs hidden sm:table-cell">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="py-2.5 px-2 text-center text-white/50 text-xs">{u.totalSessions}</td>
                      <td className="py-2.5 px-2 text-center text-white/50 text-xs hidden md:table-cell">{u.gameCount}</td>
                      <td className="py-2.5 px-2 text-center text-white/50 text-xs hidden md:table-cell">{u.achievementCount}</td>
                      <td className="py-2.5 px-2 text-center">
                        <button
                          onClick={() => toggleProOverride(u.user_id)}
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium transition-all ${
                            u.proOverride
                              ? "bg-amber-500/20 text-amber-400 hover:bg-amber-500/30"
                              : "bg-white/5 text-white/20 hover:bg-white/10 hover:text-white/40"
                          }`}
                          data-testid={`pro-toggle-${u.user_id}`}
                          title={u.proOverride ? "Revoke Pro override" : "Grant Pro override"}
                        >
                          <Crown className="w-3 h-3" />
                          {u.proOverride ? "Pro" : "—"}
                        </button>
                      </td>
                      <td className="py-2.5 px-2 text-center">
                        <button
                          onClick={() => toggleTestAccount(u.user_id)}
                          disabled={u.isAdmin}
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium transition-all ${
                            u.isTestAccount
                              ? "bg-sky-500/20 text-sky-300 hover:bg-sky-500/30"
                              : "bg-white/5 text-white/20 hover:bg-white/10 hover:text-white/40"
                          } ${u.isAdmin ? "opacity-40 cursor-not-allowed" : ""}`}
                          data-testid={`test-toggle-${u.user_id}`}
                          title={u.isAdmin ? "Admins are always excluded from usage data" : (u.isTestAccount ? "Unmark test account" : "Mark as test account (excluded from usage data)")}
                        >
                          <FlaskConical className="w-3 h-3" />
                          {u.isAdmin ? "Admin" : (u.isTestAccount ? "Test" : "—")}
                        </button>
                      </td>
                      <td className="py-2.5 px-2 text-right text-white/30 text-xs">{timeAgo(u.lastActive)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredUsers.length === 0 && (
              <p className="text-white/30 text-sm text-center py-6">No users found</p>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
}
